import { clearCurrentSession, setCurrentSession } from '@jwplayer/companion/session/cache';
import { auth, type GetOutput, type GetParams, jwApi } from '@jwplayer/companion-gateway';
import { getWuiOverlaysApi } from '@jwplayer/wui-react';

import { sitesNameCache } from '/component/site-select-input';
import { populateAccountLimits } from '/module/account';
import { redirect } from '/module/routing/module';
import { setSentryUser } from '/module/sentry';
import { getInitialActiveSiteId, setCurrentSiteId } from '/module/sites';
import { hydrateQueryCache as hydrateContentProtectionQueryCache } from '/module/sites/content-protection';
import { hydrateQueryCache as hydrateDrmEnabledQueryCache } from '/module/sites/drm-enabled';

import { SessionStore } from './index';

async function updateSiteInformation(siteId: string) {
  SessionStore.update((s) => {
    s.currentSiteId = siteId;
  });

  if (siteId) {
    await Promise.all([
      // Populate the site-specific information
      hydrateContentProtectionQueryCache(siteId),
      hydrateDrmEnabledQueryCache(siteId),
    ]);
  }
}

export async function createSession(data?: Parameters<typeof auth.login>[0]) {
  // This function should not contain (or minimize) the amount of blocking API calls. We don't
  // want a user to be unable to login because one (or more) API calls is failing.

  const session = data ? await auth.login(data) : await auth.authenticate();

  // If the session was created successfully, we need to get the user's information
  const user = (await jwApi('current-user', 'read', {})).data;

  const initialActiveSiteId = getInitialActiveSiteId(session, user);

  // Share session with companion v1 and set the active site id there
  shareSessionWithCompanionV1(session);
  setCurrentSiteId(initialActiveSiteId, session.default_site);

  // Asynchronously retrieve account limits information. It doesn't matter if this fails.
  populateAccountLimits();

  // Retrieve additional information
  await Promise.all([
    // Populate query cache for the current site
    updateSiteInformation(initialActiveSiteId),

    // Populate the sites name cache for the site-select input
    sitesNameCache.populate(),
  ]);

  // Set sentry user
  setSentryUser(session, user);

  // When auth automatically refreshes
  auth.subscribe('authenticated', (_, session) => {
    // Share any changes to the session to old companion, so that it may use
    // this session to make api calls
    shareSessionWithCompanionV1(session);

    // Ensure our local store is always up to date
    SessionStore.update((s) => {
      s.session = modifySessionForDashboard(session);
    });
  });

  // When auth refresh has failed
  auth.subscribe('refreshFailed', () => {
    cleanupSession();
  });

  // When auth token is no longer valid (computer/tab was inactive for too long)
  auth.subscribe('expired', () => {
    cleanupSession();
  });

  SessionStore.update((s) => {
    s.session = modifySessionForDashboard(session);
    s.user = user;
  });

  return session;
}

/**
 * Modify the session object for use in the dashboard.
 *
 * Will sort sites in alphabetical order.
 */
function modifySessionForDashboard<T extends GetOutput<'dashboard-scope', 'read'>>(
  session: T | undefined,
): T | undefined {
  if (typeof session === 'undefined') {
    return undefined;
  }

  return {
    ...session,
    sites: [...session.sites].sort((a, b) => {
      return Intl.Collator().compare(a.name.toLowerCase(), b.name.toLowerCase());
    }),
  };
}

export function destroySession() {
  auth.logout();

  cleanupSession(false);
}

function cleanupSession(showToast = true) {
  redirect({
    to: {
      pathname: 'login',
    },
  });

  // Clean up session in companion v1
  shareSessionWithCompanionV1(undefined);

  setSentryUser(undefined, undefined);

  sitesNameCache.reset();

  SessionStore.update((s) => {
    s.currentSiteId = undefined;
    s.session = undefined;
    s.user = undefined;
    s.accountLimits = {
      isStreamingStopped: false,
      isStorageStopped: false,
      isAdsStopped: false,
    };
  });

  if (showToast) {
    getWuiOverlaysApi().Toast.createAndOpen({
      msg: 'Your session has expired. Please log in again.',
      type: 'error',
      timeout: undefined,
    });
  }
}

export const updateCurrentUser = async (metadata: GetParams<'current-user', 'update'>['input']['metadata']) => {
  try {
    const user = (await jwApi('current-user', 'update', { input: { metadata } })).data;

    SessionStore.update((s) => {
      s.user = user;
    });

    return user;
  } catch (e: unknown) {
    getWuiOverlaysApi().Toast.createAndOpen({ msg: 'Failed to update your settings', type: 'error' });
    throw e;
  }
};

export function shareSessionWithCompanionV1(session: GetOutput<'dashboard-scope', 'read'> | undefined) {
  if (session) {
    setCurrentSession(
      // we don't need to be type-strict for the old companion
      session as never,
    );
  } else {
    clearCurrentSession();
  }
}
